import React, { useState } from 'react';
import { Shield, Download, FileSignature, Clock, Stethoscope, ChevronDown, ChevronUp, ExternalLink, BookOpen, Keyboard } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { BrandEmblem } from '../BrandEmblem';
import TutorialSlideshow from '../TutorialSlideshow';
import { toast } from 'sonner';

const BACKEND_URL = window.__WR_API_BASE__;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [regulationsOpen, setRegulationsOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Key Regulations Card */}
      <Collapsible open={regulationsOpen} onOpenChange={setRegulationsOpen}>
        <Card className="border-border bg-secondary/50" data-testid="regulations-card">
          <CollapsibleTrigger asChild>
            <CardContent className="py-3 cursor-pointer hover:bg-secondary/70 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileSignature className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-foreground">Key Regulations</p>
                    {regulationsOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    AR 25-50, AR 40-501, AR 600-9, DoWI 6130.03
                  </p>
                </div>
              </div>
            </CardContent>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 pb-3 border-t border-border/50">
              <div className="space-y-2 text-xs">
                {/* Regulations with hyperlinks */}
                <div className="space-y-1.5 mt-2">
                  <p className="text-muted-foreground">
                    <a href="https://armypubs.army.mil/epubs/DR_pubs/DR_a/ARN30948-AR_25-50-000-WEB-1.pdf" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-primary inline-flex items-center gap-1">
                      AR 25-50 <ExternalLink className="w-3 h-3" />
                    </a> - Correspondence
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://armypubs.army.mil/epubs/DR_pubs/DR_a/ARN40026-AR_40-501-002-WEB-4.pdf" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-primary inline-flex items-center gap-1">
                      AR 40-501 <ExternalLink className="w-3 h-3" />
                    </a> - Medical Fitness / Profiles
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://armypubs.army.mil/epubs/DR_pubs/DR_a/ARN40774-AR_600-9-001-WEB-3.pdf" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-primary inline-flex items-center gap-1">
                      AR 600-9 <ExternalLink className="w-3 h-3" />
                    </a> - Army Body Composition Program (ABCP)
                  </p>
                </div>
                
                <div className="space-y-1.5 pt-2 border-t border-border/30">
                  <p className="text-muted-foreground">
                    <a href="https://www.esd.whs.mil/Portals/54/Documents/DD/issuances/dodi/613003v1p.pdf" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-primary inline-flex items-center gap-1">
                      DoWI 6130.03 Vol 1 <ExternalLink className="w-3 h-3" />
                    </a> - Accession Standards (6 Feb 2026)
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.esd.whs.mil/Portals/54/Documents/DD/issuances/dodi/613003v2p.pdf" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-primary inline-flex items-center gap-1">
                      DoWI 6130.03 Vol 2 <ExternalLink className="w-3 h-3" />
                    </a> - Retention / MEB (6 Feb 2026)
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.esd.whs.mil/Portals/54/Documents/DD/issuances/dodi/649007p.pdf" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-primary inline-flex items-center gap-1">
                      DoWI 6490.07 <ExternalLink className="w-3 h-3" />
                    </a> - Deployment-Limiting Conditions
                  </p>
                </div>
                
                <div className="space-y-1.5 pt-2 border-t border-border/30">
                  <p className="text-muted-foreground">
                    <a href="https://medcoeckapwstorprd01.blob.core.windows.net/pfi-images/dbimages/p21019.pdf" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-primary inline-flex items-center gap-1">
                      OTSG MEDCOM 21-019 <ExternalLink className="w-3 h-3" />
                    </a> - BH Profiling / Psychotropics
                  </p>
                  <p className="text-muted-foreground">
                    <span className="font-bold text-foreground">AD 2025-02</span> - Pregnancy / Parenthood / Miscarriage
                  </p>
                  <p className="text-muted-foreground">
                    <span className="font-bold text-foreground">AD 2025-17/18</span> - Body Composition Updates
                  </p>
                </div>
                
                {/* Civilian/Disability References */}
                <div className="space-y-1.5 pt-2 border-t border-border/30">
                  <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wide">Civilian / Disability</p>
                  <p className="text-muted-foreground">
                    <a href="https://www.ssa.gov/disability/professionals/bluebook/" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-blue-400 inline-flex items-center gap-1">
                      SSA Blue Book <ExternalLink className="w-3 h-3" />
                    </a> - SSDI/SSI Impairment Listings
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.dol.gov/agencies/whd/fmla" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-blue-400 inline-flex items-center gap-1">
                      FMLA <ExternalLink className="w-3 h-3" />
                    </a> - Family and Medical Leave Act
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.ada.gov/" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-blue-400 inline-flex items-center gap-1">
                      ADA <ExternalLink className="w-3 h-3" />
                    </a> - Americans with Disabilities Act
                  </p>
                </div>
                
                {/* VA Benefits References */}
                <div className="space-y-1.5 pt-2 border-t border-border/30">
                  <p className="text-[10px] font-semibold text-green-500 uppercase tracking-wide">VA Benefits & Claims</p>
                  <p className="text-muted-foreground">
                    <a href="https://www.va.gov/disability/how-to-file-claim/" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-green-500 inline-flex items-center gap-1">
                      VA Disability Claims <ExternalLink className="w-3 h-3" />
                    </a> - How to file a claim
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.va.gov/decision-reviews/" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-green-500 inline-flex items-center gap-1">
                      VA Decision Reviews <ExternalLink className="w-3 h-3" />
                    </a> - Appeals, HLR, Supplemental Claims
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.ecfr.gov/current/title-38/chapter-I/part-4" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-green-500 inline-flex items-center gap-1">
                      38 CFR Part 4 <ExternalLink className="w-3 h-3" />
                    </a> - VA Rating Schedule
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.va.gov/find-forms/" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-green-500 inline-flex items-center gap-1">
                      VA Form Finder <ExternalLink className="w-3 h-3" />
                    </a> - Search all VA forms
                  </p>
                  <p className="text-muted-foreground">
                    <a href="https://www.archives.gov/veterans/military-service-records" target="_blank" rel="noopener noreferrer" className="font-bold text-foreground hover:text-green-500 inline-flex items-center gap-1">
                      Military Records (SF-180) <ExternalLink className="w-3 h-3" />
                    </a> - Request service records
                  </p>
                </div>
                
                <p className="text-muted-foreground/60 pt-2 italic">
                  ARs: armypubs.army.mil • DoWIs: esd.whs.mil • SSA: ssa.gov
                </p>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* AR 25-50 Templates Card */}
      <Collapsible open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <Card className="border-border bg-secondary/50" data-testid="templates-card">
          <CollapsibleTrigger asChild>
            <CardContent className="py-3 cursor-pointer hover:bg-secondary/70 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Download className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-foreground">AR 25-50 Templates</p>
                    {templatesOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Word templates, salutations guide
                  </p>
                </div>
              </div>
            </CardContent>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 pb-3 border-t border-border/50">
              {/* AR 25-50 Template Downloads */}
              <div className="mt-2">
                <p className="text-xs font-bold text-foreground mb-2">Download Word Templates</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    onClick={() => window.open(`${API}/word-templates/figure2`, '_blank')}
                  >
                    <Download className="w-3 h-3 mr-1 shrink-0" />
                    Fig 2-1 Memo
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    onClick={() => window.open(`${API}/word-templates/figure2_2`, '_blank')}
                  >
                    <Download className="w-3 h-3 mr-1 shrink-0" />
                    Fig 2-2 Multi-Page
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    onClick={() => window.open(`${API}/word-templates/figure2_4`, '_blank')}
                  >
                    <Download className="w-3 h-3 mr-1 shrink-0" />
                    Fig 2-4 THRU
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    onClick={() => window.open(`${API}/word-templates/figure3`, '_blank')}
                  >
                    <Download className="w-3 h-3 mr-1 shrink-0" />
                    Fig 3-1 Letter
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    onClick={() => window.open(`${API}/word-templates/medical_memo`, '_blank')}
                  >
                    <Download className="w-3 h-3 mr-1 shrink-0" />
                    Medical Memo
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs justify-start"
                    onClick={() => window.open(`${API}/word-templates/profile_memo`, '_blank')}
                  >
                    <Download className="w-3 h-3 mr-1 shrink-0" />
                    Profile Memo
                  </Button>
                </div>
              </div>
              
              {/* Salutations Quick Reference */}
              <div className="mt-3 pt-2 border-t border-border/30">
                <p className="text-xs font-bold text-foreground mb-1">Letter Salutations (Appendix C)</p>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <p><span className="text-foreground">Known:</span> Dear Mr./Ms. [Name]: → Sincerely,</p>
                  <p><span className="text-foreground">Unknown:</span> Dear Sir or Madam: → Sincerely,</p>
                  <p><span className="text-foreground">HR Dept:</span> Dear Human Resources: → Sincerely,</p>
                  <p><span className="text-foreground">Official:</span> Dear Congressman/Senator [Name]:</p>
                  <p className="text-muted-foreground/70 italic mt-1">Letters: Signature block LEFT • Memos: RIGHT</p>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Keyboard Shortcuts Reference Card */}
      <Card className="border-border bg-secondary/50" data-testid="shortcuts-card">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Keyboard className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-foreground mb-1.5">Keyboard Shortcuts</p>
              <div className="space-y-1">
                {[
                  { keys: 'Ctrl + Enter', action: 'Generate Docs' },
                  { keys: 'Ctrl + Shift + C', action: 'Copy All' },
                  { keys: 'Ctrl + Shift + N', action: 'Next Patient' },
                  { keys: 'Ctrl + /', action: 'Toggle Shortcut Panel' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{s.action}</span>
                    <kbd className="text-[10px] font-mono font-semibold bg-secondary border border-border rounded px-1.5 py-0.5 text-muted-foreground">{s.keys}</kbd>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Abbreviations + Verbatim Signal */}
      <Card className="border-border bg-secondary/30" data-testid="abbreviations-card">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="w-3.5 h-3.5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-bold text-foreground">Abbreviations & Signals</p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                yo, M/F, AD, ADSM, h/o, w/, w/o, s/p, r/o, eval/TX, NAD, ROM, PRN
              </p>
              <p className="text-xs text-amber-400 mt-1.5 font-semibold">
                &gt;&gt; = Keep Verbatim
              </p>
              <p className="text-[10px] text-muted-foreground">
                Start any line with <code className="bg-amber-500/20 text-amber-300 px-1 rounded">&gt;&gt;</code> to keep it word-for-word in the output. AI will clean grammar but not remove or rephrase the content. Works until end of that line.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dot Phrase Library */}
      <Collapsible>
        <Card className="border-border bg-secondary/50 md:col-span-2" data-testid="dot-phrase-library-card">
          <CollapsibleTrigger asChild>
            <CardContent className="py-3 cursor-pointer hover:bg-secondary/70 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-3.5 h-3.5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-foreground">Dot Phrase Library</p>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Type in notes + space to expand &bull; .bhcry .keyresults .PEadd .dm2 .htn .rtd .qtrs +12 more
                  </p>
                </div>
              </div>
            </CardContent>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 pb-3 border-t border-border/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2">
                {[
                  { phrase: '.bhcry', desc: 'BH therapeutic rapport', auto: 'F32, F41, F43, F90' },
                  { phrase: '.keyresults', desc: 'Key Results review template', auto: null },
                  { phrase: '.PEadd', desc: 'Full PE template (all systems)', auto: null },
                  { phrase: '.dm2', desc: 'Diabetes management', auto: null },
                  { phrase: '.htn', desc: 'HTN management', auto: null },
                  { phrase: '.rtd', desc: 'Return to duty', auto: null },
                  { phrase: '.qtrs', desc: 'Quarters (24/48/72h)', auto: null },
                  { phrase: '.tbiPRA', desc: 'TBI PRA Protocol (6 stages + NSI)', auto: null },
                  { phrase: '.tbiNSI', desc: 'NSI 22-question form + scoring + patient watch list', auto: null },
                  { phrase: '.cacs', desc: 'Coronary calcium score referral', auto: null },
                  { phrase: '.educac', desc: 'CAC scan education', auto: null },
                  { phrase: '.eduMESA', desc: 'MESA coronary age score', auto: null },
                  { phrase: '.eduhld', desc: 'HDL improvement education', auto: null },
                  { phrase: '.lowdoseCT', desc: 'USPSTF lung CT screening', auto: null },
                  { phrase: '.colonoscopy', desc: 'USPSTF colorectal screening', auto: null },
                  { phrase: '.mammo', desc: 'USPSTF mammography', auto: null },
                  { phrase: '.pap', desc: 'USPSTF cervical screening', auto: null },
                  { phrase: '.psa', desc: 'USPSTF prostate screening', auto: null },
                  { phrase: '.aaa', desc: 'USPSTF AAA screening', auto: null },
                ].map(dp => (
                  <div key={dp.phrase} className="flex items-center gap-2 py-1 border-b border-border/20 last:border-0">
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(dp.phrase + ' ');
                          toast.success(`Copied ${dp.phrase} — paste in notes`, { duration: 2000 });
                        } catch { toast.error('Copy failed'); }
                      }}
                      className="text-[11px] font-bold text-primary shrink-0 w-[90px] text-left hover:text-amber-400 transition-colors cursor-pointer"
                      title={`Tap to copy ${dp.phrase}`}
                    >
                      {dp.phrase}
                    </button>
                    <span className="text-[11px] text-muted-foreground flex-1 truncate">{dp.desc}</span>
                    {dp.auto && (
                      <span className="text-[9px] px-1 py-0.5 bg-amber-500/20 text-amber-400 rounded shrink-0">auto</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground/60 mt-2 italic">
                Auto-triggered by ICD-10: J30 (Allergies), J01/J06 (Sinusitis), K21 (GERD), L50 (Urticaria), F-codes (BH rapport)
              </p>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export const CreatorFooter = ({ onNavigate }) => {
  const [showPatientTutorial, setShowPatientTutorial] = useState(false);
  const [showProviderTutorial, setShowProviderTutorial] = useState(false);
  
  return (
    <footer className="bg-card border-t border-border mt-8 py-8 pb-24 sm:pb-8 w-full">
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="relative text-center min-h-[200px] flex flex-col justify-center">
          {/* Content */}
          <div className="relative z-10 space-y-3">
            {/* Brand Emblem - Single source of truth */}
            <div className="flex items-center justify-center mb-4 relative z-10" style={{ '--brand-text-color': 'var(--foreground)' }}>
              <BrandEmblem size="lg" />
            </div>
            {/* Tagline - right after branding */}
            <p className="text-muted-foreground text-xs">Because the system won't change — but your workflow can. Built from burnout. Built to last.</p>
            
            {/* Tutorial Buttons - Interactive Slideshow with Screenshots */}
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <button 
                onClick={() => setShowPatientTutorial(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                  color: '#1e293b'
                }}
              >
                <BookOpen className="w-4 h-4" />
                Patient Tutorial
              </button>
              <button 
                onClick={() => setShowProviderTutorial(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                  color: '#1e293b'
                }}
              >
                <BookOpen className="w-4 h-4" />
                Provider Tutorial
              </button>
            </div>
            
            {/* Divider line */}
            <div className="pt-4 border-t border-border/50 mt-4">
              {/* Credentials below the line */}
              <div className="text-sm text-muted-foreground space-y-1 mt-3">
                <p className="font-semibold text-foreground text-base">
                  Created by Dr. Lakia S. Stewart
                </p>
                <p>DNP, FNP-C, WHNP-BC</p>
                <p>Family & Women's Health Nurse Practitioner</p>
                <p className="text-xs">Alumni of:</p>
                <p className="text-xs">Uniformed Services University of the Health Sciences</p>
                <p className="text-xs">Daniel K. Inouye Graduate School of Nursing</p>
              </div>
              
              {/* Legal Links */}
              <div className="flex flex-wrap justify-center gap-4 mt-4 pt-3 border-t border-border/30">
                <button onClick={() => onNavigate && onNavigate('terms')} className="text-xs text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline" data-testid="footer-terms-link">Terms &amp; Conditions</button>
                <button onClick={() => onNavigate && onNavigate('privacy')} className="text-xs text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline" data-testid="footer-privacy-link">Privacy Policy</button>
                <button onClick={() => onNavigate && onNavigate('hipaa')} className="text-xs text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline" data-testid="footer-hipaa-link">HIPAA Notice</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tutorial Slideshows with Screenshots */}
      <TutorialSlideshow 
        type="patient" 
        show={showPatientTutorial} 
        onClose={() => setShowPatientTutorial(false)} 
      />
      <TutorialSlideshow 
        type="provider" 
        show={showProviderTutorial} 
        onClose={() => setShowProviderTutorial(false)} 
      />
    </footer>
  );
};

export default Footer;
